import { useEffect, useState } from 'react';

import { api } from '../lib/api';

/** "Continue with Google", plus the divider that separates it from the form.
 *
 *  Renders nothing at all until the deployment confirms an OAuth client is
 *  configured. A visible button that dead-ends on Google's own error page is
 *  worse than no button, and whether credentials exist is a deploy-time fact the
 *  browser cannot infer.
 *
 *  A plain link, not a fetch: /v1/auth/google/start answers a 302 to Google, so
 *  the browser navigates and there is no popup, no CORS preflight and no token
 *  handling in JavaScript. The session cookie is set by the callback, server
 *  side, and stays HttpOnly throughout.
 */
export function GoogleButton({ label }: { label: string }) {
  const [available, setAvailable] = useState(false);

  useEffect(() => {
    let alive = true;
    api.auth
      .providers()
      .then((result) => {
        if (alive) setAvailable(result.google);
      })
      // A failure here means the button stays hidden, which is the safe way to
      // be wrong: the email and password form is still right there.
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, []);

  if (!available) return null;

  return (
    <>
      <a className="btn-google" href="/v1/auth/google/start">
        {/* Google's mark, inlined. A remote image would be blocked by the CSP,
            and the file is small enough that a request for it is not worth it. */}
        <svg className="google-mark" viewBox="0 0 18 18" aria-hidden="true" focusable="false">
          <path
            fill="#4285F4"
            d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.91c1.7-1.57 2.69-3.88 2.69-6.62Z"
          />
          <path
            fill="#34A853"
            d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.91-2.26c-.81.54-1.84.86-3.05.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.34A9 9 0 0 0 9 18Z"
          />
          <path
            fill="#FBBC05"
            d="M3.97 10.72a5.41 5.41 0 0 1 0-3.44V4.94H.96a9 9 0 0 0 0 8.12l3.01-2.34Z"
          />
          <path
            fill="#EA4335"
            d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.59C13.46.89 11.43 0 9 0A9 9 0 0 0 .96 4.94l3.01 2.34C4.68 5.16 6.66 3.58 9 3.58Z"
          />
        </svg>
        {label}
      </a>

      {/* Decorative: the rule and the word "or" are one visual separator, and
          reading "or" aloud between two options adds nothing. */}
      <div className="or-divider" aria-hidden="true">
        <span>or</span>
      </div>
    </>
  );
}
